import { uniqWith } from 'es-toolkit';
import React, { useMemo } from 'react';

import { BearingEnvelope } from '@paragon/contract-library';
import { distanceEquals, Point2D, Polygon2D } from '@paragon/geometry-library';
import { calculateBearingEnvelopeShape } from '@paragon/layout';
import { notNullish } from '@paragon/utilities';

import { convertFromLayoutToDesignCoordinates } from '../../../library/layout-geometry/coordinates';
import { layoutColors } from '../layoutColors';
import { ManySegment } from '../Segments';

interface Props {
  envelopes: BearingEnvelope[];
}

/**
 * Renders vertical segments where overlapping bearing envelope intersect. These "virtual edges" exist only where the
 *   faces of two different envelopes cross each other so they are not part of either envelope's own geometry.
 */
export function BearingEnvelopeIntersectionEdges({ envelopes }: Props) {
  const segments = useMemo(() => {
    const shapesAndEnvelopes = envelopes
      .map((envelope) => {
        const shape = calculateBearingEnvelopeShape(envelope);
        return shape ? { shape, envelope } : null;
      })
      .filter(notNullish);

    const intersectionPoints: { point: Point2D; bottom: number; top: number }[] = [];

    for (let i = 0; i < shapesAndEnvelopes.length; i++) {
      for (let j = i + 1; j < shapesAndEnvelopes.length; j++) {
        const a = shapesAndEnvelopes[i];
        const b = shapesAndEnvelopes[j];

        const bottom = Math.max(a.envelope.bottom, b.envelope.bottom);
        const top = Math.min(a.envelope.top, b.envelope.top);
        if (top <= bottom) continue;

        if (!Polygon2D.areOverlappingConvex(a.shape, b.shape)) continue;

        for (const edgeA of a.shape.edges) {
          for (const edgeB of b.shape.edges) {
            const point = edgeA.intersectWithSegment(edgeB);
            // We only care about only interior intersections. Intersections at endpoints already have a rendered
            //   vertical edge on the bearing envelope visual itself.
            if (point && edgeA.containsOnInside(point) && edgeB.containsOnInside(point)) {
              intersectionPoints.push({ point, bottom, top });
            }
          }
        }
      }
    }

    const deduplicated = uniqWith(
      intersectionPoints,
      (a, b) => a.point.equals(b.point) && distanceEquals(a.bottom, b.bottom) && distanceEquals(a.top, b.top),
    );

    return deduplicated.map(({ point, bottom, top }) => ({
      start: convertFromLayoutToDesignCoordinates(point.to3D(bottom)).toArray(),
      end: convertFromLayoutToDesignCoordinates(point.to3D(top)).toArray(),
      color: layoutColors.solidMeshEdges,
    }));
  }, [envelopes]);

  if (segments.length === 0) return null;

  return <ManySegment segments={segments} />;
}
